import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import campaignService from '../services/campaignService';

/**
 * Parse value from streaming - handles JSON strings for arrays and objects
 */
const parseStreamedValue = (value, field_id, entityType) => {
  // If it's already an object or array, return as-is
  if (typeof value === 'object' && value !== null) {
    return value;
  }
  
  // If it's a string, try to parse as JSON for arrays/objects
  if (typeof value === 'string') {
    // Check if it looks like JSON
    const trimmed = value.trim();
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || 
        (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        return JSON.parse(trimmed);
      } catch (e) {
        console.warn(`[useCustomFields:${entityType}] Failed to parse JSON for field ${field_id}:`, e);
        // Return as string if parsing fails
        return value;
      }
    }
  }
  
  // Return as-is for other types (numbers, booleans, etc.)
  return value;
};

/**
 * Check if a value is considered "meaningful" (not empty)
 */
const hasNonEmptyValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  // Check for empty arrays
  if (Array.isArray(value) && value.length === 0) {
    return false;
  }
  // Check for empty objects
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
    return false;
  }
  return true;
};

/**
 * Custom hook for managing custom fields (both company and contact fields)
 * Handles field definitions, field values, streaming updates, and optimistic updates
 * 
 * @param {string} campaignId - The campaign ID
 * @param {string} entityType - Either 'companies' or 'contacts'
 * @returns {object} Hook state and functions
 */
export const useCustomFields = (campaignId, entityType = 'companies') => {
  // Field definitions: array of { field_id, field_name, display_name, config_id, status }
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  
  // Field values: Map of membership_id -> { field_id: value }
  const [fieldValues, setFieldValues] = useState(new Map());
  
  // Track membership_ids that have preview data (for highlighting)
  const [previewMembershipIds, setPreviewMembershipIds] = useState(new Set());
  
  // Track preview config ID (status='draft')
  const [previewConfigId, setPreviewConfigId] = useState(null);
  
  // Version counter for triggering re-renders when field values change
  const [fieldValuesVersion, setFieldValuesVersion] = useState(0);
  
  // Use ref to avoid stale closure in handleValueUpdate
  const previewConfigIdRef = useRef(null);
  
  // Sync ref with state
  useEffect(() => {
    previewConfigIdRef.current = previewConfigId;
  }, [previewConfigId]);
  
  // Loading state
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  
  // Fetch existing field definitions on mount
  const fetchDefinitions = useCallback(async () => {
    if (!campaignId) return;
    
    setIsLoadingFields(true);
    try {
      const response = await api.get(`/api/custom-fields/campaigns/${campaignId}/fields`, {
        params: { entity_type: entityType }
      });
      
      const fields = response.data.fields || [];
      const formattedFields = fields.map(field => ({
        field_id: field._id || field.field_id,
        field_name: field.field_name,
        display_name: field.display_name,
        config_id: field.config_id,
        status: field.status || 'active',
        needs_more: field.needs_more || false,
        source: field.source || null,
      }));
      
      setFieldDefinitions(formattedFields);
      
      // Check for preview fields and set preview config ID
      const previewField = formattedFields.find(f => f.status === 'preview');
      if (previewField && previewField.config_id) {
        setPreviewConfigId(previewField.config_id);
      } else {
        // Only clear preview config if we explicitly found no preview fields
        // This preserves preview state during user transitions (e.g., anonymous to registered)
        // Only clear if there were no preview fields AND we had existing field definitions
        if (formattedFields.length > 0) {
          setPreviewConfigId(null);
        }
        // If formattedFields is empty, don't clear preview state - it might be a temporary state
      }
    } catch (error) {
      console.error('Error fetching field definitions:', error);
      // Don't throw - just log. Empty definitions is valid state.
      // Don't clear preview state on error - preserve it for retry
    } finally {
      setIsLoadingFields(false);
    }
  }, [campaignId, entityType]);
  
  useEffect(() => {
    fetchDefinitions();
  }, [fetchDefinitions]);
  
  /**
   * Initialize field definitions from an external source (e.g., campaign data API response)
   * This allows pre-populating definitions without waiting for the separate API call
   * Pass an empty array to clear all field definitions
   */
  const initializeDefinitions = useCallback((definitions) => {
    if (!definitions || !Array.isArray(definitions)) {
      return;
    }
    
    // Allow empty array to clear definitions
    if (definitions.length === 0) {
      setFieldDefinitions([]);
      setPreviewConfigId(null);
      return;
    }
    
    const formattedDefs = definitions.map(field => ({
      field_id: field._id || field.field_id,
      field_name: field.field_name,
      display_name: field.display_name,
      config_id: field.config_id,
      status: field.status || 'active',
      needs_more: field.needs_more || false,
      source: field.source || null,
      ...(field.output_schema && { output_schema: field.output_schema })
    }));
    
    setFieldDefinitions(formattedDefs);
    
    // Check for preview fields - only 'preview' status shows the banner
    // 'generating' means already approved, so no banner needed
    const previewField = formattedDefs.find(f => f.status === 'preview');
    
    if (previewField && previewField.config_id) {
      setPreviewConfigId(previewField.config_id);
    } else {
      setPreviewConfigId(null);
    }
  }, [entityType]);
  
  /**
   * Handle streaming custom field definition updates
   * Called when custom_field_definition events arrive
   */
  const handleDefinitionUpdate = useCallback((eventData) => {
    const { field_ids, field_names, display_names, config_id, is_new, output_schemas, source } = eventData;
    
    if (!field_ids || !field_names || !display_names) {
      console.warn('Invalid field definition event data', eventData);
      return;
    }
    
    const newDefinitions = field_ids.map((id, idx) => ({
      field_id: id,
      field_name: field_names[idx],
      display_name: display_names[idx],
      config_id,
      status: is_new ? 'preview' : 'active',
      needs_more: false, // Streaming updates reset needs_more
      source: source || null,
      // Include output_schema if provided (for type validation)
      ...(output_schemas && output_schemas[idx] && { output_schema: output_schemas[idx] })
    }));
    
    // If this is a preview, track the config ID
    if (is_new && config_id) {
      setPreviewConfigId(config_id);
    }
    
    setFieldDefinitions(prev => {
      // Avoid duplicates - check if field_id already exists
      const existingIds = new Set(prev.map(f => f.field_id));
      const uniqueNewDefs = newDefinitions.filter(def => !existingIds.has(def.field_id));
      
      if (uniqueNewDefs.length === 0) {
        return prev; // No new definitions to add
      }
      
      return [...prev, ...uniqueNewDefs];
    });
  }, [entityType]);
  
  /**
   * Handle streaming custom field value updates
   * Called when *_custom_field_update events arrive
   * Expects data format: { membership_id, field_id, value, type }
   */
  const handleValueUpdate = useCallback((eventData) => {
    const { membership_id, field_id, value } = eventData;
    
    if (!membership_id || !field_id) {
      console.warn('Invalid field value update event data', eventData);
      return;
    }
    
    // Parse value to handle JSON strings for arrays/objects
    const parsedValue = parseStreamedValue(value, field_id, entityType);
    
    setFieldValues(prev => {
      const newMap = new Map(prev);
      const entityValues = newMap.get(membership_id) || {};
      newMap.set(membership_id, { ...entityValues, [field_id]: parsedValue });
      return newMap;
    });
    
    // Increment version to trigger re-renders
    setFieldValuesVersion(prev => prev + 1);
    
    // Track this membership_id as having preview data
    // Use ref to avoid stale closure issue
    if (previewConfigIdRef.current) {
      setPreviewMembershipIds(prev => new Set([...prev, membership_id]));
    }
  }, [entityType]);
  
  /**
   * Enrich entity data with custom field values
   * Merges field values into entity objects based on field definitions
   */
  const enrichEntityData = useCallback((entities) => {
    if (!entities || entities.length === 0) {
      return entities;
    }
    
    if (fieldDefinitions.length === 0) {
      // No custom fields defined - return entities as-is
      return entities;
    }
    
    // Track membership IDs with preview data
    // Only check for 'preview' status - 'generating' means already approved
    const previewMembershipIdsSet = new Set();
    const previewFieldIds = new Set(
      fieldDefinitions.filter(f => f.status === 'preview').map(f => f.field_id)
    );
    
    const enrichedEntities = entities.map(entity => {
      const values = fieldValues.get(entity.membership_id) || {};
      const custom_fields = {};
      const custom_fields_metadata = {};
      let hasDraftData = false;
      
      // Build custom_fields object with field_id as key (immutable, guaranteed unique)
      fieldDefinitions.forEach(fieldDef => {
        // Use nullish coalescing to preserve falsy values like 0 and false
        const value = values[fieldDef.field_id] ?? entity.custom_fields?.[fieldDef.field_id];
        custom_fields[fieldDef.field_id] = value !== null && value !== undefined ? value : '';
        custom_fields_metadata[fieldDef.field_id] = {
          field_id: fieldDef.field_id,
          config_id: fieldDef.config_id,
          status: fieldDef.status,
          display_name: fieldDef.display_name
        };
        
        // Check if this entity has preview field data (meaningful value)
        // Only 'preview' status - 'generating' means already approved
        const isPreviewField = fieldDef.status === 'preview';
        if (isPreviewField && hasNonEmptyValue(value)) {
          hasDraftData = true;
        }
      });
      
      // Track this membership_id if it has preview data
      if (hasDraftData && entity.membership_id) {
        previewMembershipIdsSet.add(entity.membership_id);
      }
      
      return {
        ...entity,
        custom_fields,
        custom_fields_metadata
      };
    });
    
    // Update preview membership IDs if we have preview fields
    if (previewFieldIds.size > 0 && previewMembershipIdsSet.size > 0) {
      setPreviewMembershipIds(previewMembershipIdsSet);
    }
    
    return enrichedEntities;
  }, [fieldDefinitions, fieldValues, entityType]);
  
  /**
   * Update a field value with optimistic update
   * @param {string} membership_id - The entity's membership ID
   * @param {string} field_id - The field ID to update
   * @param {string} newValue - The new value
   */
  const updateFieldValue = async (membership_id, field_id, newValue) => {
    // Store old value for rollback
    const oldValues = fieldValues.get(membership_id) || {};
    const oldValue = oldValues[field_id];
    
    // Optimistic update
    setFieldValues(prev => {
      const newMap = new Map(prev);
      const entityValues = newMap.get(membership_id) || {};
      newMap.set(membership_id, { ...entityValues, [field_id]: newValue });
      return newMap;
    });
    
    // Increment version to trigger re-renders
    setFieldValuesVersion(prev => prev + 1);
    
    try {
      await campaignService.updateCustomFieldValue(
        campaignId,
        field_id,
        membership_id,
        newValue,
        entityType
      );
    } catch (error) {
      // Rollback on error
      setFieldValues(prev => {
        const newMap = new Map(prev);
        if (oldValue !== undefined) {
          const entityValues = newMap.get(membership_id) || {};
          newMap.set(membership_id, { ...entityValues, [field_id]: oldValue });
        } else {
          // Remove the field if it didn't exist before
          const entityValues = newMap.get(membership_id) || {};
          const { [field_id]: removed, ...rest } = entityValues;
          newMap.set(membership_id, rest);
        }
        return newMap;
      });
      
      // Increment version for rollback too
      setFieldValuesVersion(prev => prev + 1);
      
      throw error; // Re-throw so caller can show error toast
    }
  };
  
  /**
   * Clear preview state (used after approve/reject/discard).
   *
   * Also drops cached ``fieldValues`` so ``enrichEntityData`` uses the latest
   * ``entity.custom_fields`` from the API. Otherwise streamed preview cells
   * keep overriding server data and compliance chips stay wrong after restore.
   */
  const clearPreviewState = useCallback(() => {
    setPreviewConfigId(null);
    setPreviewMembershipIds(new Set());
    setFieldValues(new Map());
    setFieldValuesVersion((prev) => prev + 1);
  }, []);

  /**
   * Optimistically remove a field definition by ID (used after deletion)
   */
  const removeFieldDefinition = useCallback((fieldId) => {
    setFieldDefinitions(prev => prev.filter(f => f.field_id !== fieldId));
    // Also clear any cached values for that field
    setFieldValues(prev => {
      const newMap = new Map();
      for (const [mid, vals] of prev.entries()) {
        const { [fieldId]: _removed, ...rest } = vals;
        newMap.set(mid, rest);
      }
      return newMap;
    });
    setFieldValuesVersion(prev => prev + 1);
  }, []);
  
  return {
    fieldDefinitions,
    fieldValues,
    fieldValuesVersion,
    isLoadingFields,
    previewConfigId,
    previewMembershipIds,
    enrichEntityData,
    updateFieldValue,
    handleDefinitionUpdate,
    handleValueUpdate,
    clearPreviewState,
    initializeDefinitions,
    refetchDefinitions: fetchDefinitions,
    removeFieldDefinition,
  };
};

export default useCustomFields;

